
import { MoreOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, Menu, message, Modal, Space, Tabs } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import APITokenUsersApiService from '../../api/APITokenUsersApiService';
import AuthorizedContent from '../../components/AuthorizedContent';
import EditCancelButton from '../../components/buttons/EditCancelButton';
import APITokenUserDetailsForm from '../../components/forms/APITokenUserDetailsForm';
import UserRolesForm from '../../components/forms/UserRolesForm';
import Breadcrumbs from '../../config/Breadcrumbs';
import Routes from '../../config/Routes';
import FeatureFlag from '../../consts/FeatureFlag';
import * as DeleteAPITokenUserHandler from '../../handlerModels/DeleteAPITokenUserHandler';
import * as GetAPITokenUserDetailsHandler from '../../handlerModels/GetAPITokenUserDetailsHandler';
import APITokenUserDTO from '../../models/APITokenUserDTO';
import UserSecurity from '../../models/UserSecurity';
import HeaderPortal from '../../portals/HeaderPortal';
import BaseFormState from '../../redux/bases/BaseFormState';
import { StateStoreModel } from '../../redux/state/StateStoreModel';
import Guid from '../../utils/Guid';
import HistoryUtil from '../../utils/HistoryUtil';
import PageStayPrompt from '../../utils/PageStayPrompt';
import ParameterUtil from '../../utils/ParameterUtil';

interface APITokenUserDetailsState extends BaseFormState {
  apiTokenUserId: string;
  apiTokenUser: APITokenUserDTO;
  activeTab: string;
}

interface APITokenUserDetailsProps {
  currentUser: UserSecurity | null;
}

class APITokenUserDetailsPage extends React.Component<APITokenUserDetailsProps & RouteComponentProps<RouteObject>, APITokenUserDetailsState> {
  private static readonly API_TOKEN_USER_DETAILS = 'API_TOKEN_USER_DETAILS';
  private static readonly API_TOKEN_USER_ROLES = 'API_TOKEN_USER_ROLES';

  private readonly _apiTokenUserDetailsRef = React.createRef<APITokenUserDetailsForm>();
  private readonly _userRolesRef = React.createRef<UserRolesForm>();

  constructor(props: APITokenUserDetailsProps & RouteComponentProps<RouteObject>) {
    super(props);

    this.state = {
      apiTokenUserId: Guid.Empty(),
      apiTokenUser: APITokenUserDTO.create(),
      editings: new Map<string, boolean>(),
      activeTab: 'details'
    };
  }

  componentDidMount() {
    const id = ParameterUtil.getPathPart(this.props.match, 'id');
    const activeTab = ParameterUtil.getParameter('activeTab', 'details');
    this.setState({ activeTab: activeTab });

    if (id == 'new') {
      this.toggleIsEditing(APITokenUserDetailsPage.API_TOKEN_USER_DETAILS, true);
    }
    else {
      this.loadAPITokenUser(id);
    }
  }

  private loadAPITokenUser = (id: string) => {
    this.setState({ loading: true, apiTokenUserId: id });
    APITokenUsersApiService.getAPITokenUserDetails(id)
      .then((results: GetAPITokenUserDetailsHandler.Result) => {
        if (results.apiTokenUser) {
          this.setState({
            apiTokenUser: results.apiTokenUser
          });
        }
      })
      .finally(() => {
        this.setState({ loading: false })
      });
  }

  private toggleIsEditing = (key: string, value?: boolean) => {
    if (this.state.editings) {
      const editings = this.state.editings;

      if (value !== undefined) {
        editings.set(key, value);
      }
      else {
        editings.set(key, !editings.get(key) ?? true);
      }

      if (!editings.get(key)) {
        switch (key) {
          case APITokenUserDetailsPage.API_TOKEN_USER_DETAILS:
            this._apiTokenUserDetailsRef.current?.resetForm();
            break;
          case APITokenUserDetailsPage.API_TOKEN_USER_ROLES:
            this._userRolesRef.current?.resetForm();
            break;
        }
      }

      this.setState({ editings: editings });
    }
  }

  private isEditingCheck = () => {
    if (this.state.editings) {
      return Array.from(this.state.editings.values()).includes(true);
    }

    return false;
  };

  private userDetailsSaved = (id: string) => {
    this.toggleIsEditing(APITokenUserDetailsPage.API_TOKEN_USER_DETAILS, false);

    if (id != this.state.apiTokenUserId) {
      HistoryUtil.replace(Routes.generate(Routes.API_TOKEN_USER_DETAILS, { id: id }));
    }
    else {
      this.loadAPITokenUser(id);
    }
  }

  private deleteConfirmation = (id: string) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this API Token User?',
      okText: 'Delete',
      onOk: () => this.deleteAPITokenUser(id)
    });
  }

  private deleteAPITokenUser = (apiTokenUserId: string) => {
    this.setState({ submitting: true });

    const request = DeleteAPITokenUserHandler.Request.create({
        apiTokenUserId: apiTokenUserId,
    });

    APITokenUsersApiService.deleteAPITokenUser(request)
      .then((result: DeleteAPITokenUserHandler.Result | null) => {
        this.setState({
          error: !result?.succeeded,
          message: result?.errors.join('\n'),
          fieldErrors: result?.fieldErrors,
          altered: !result?.succeeded
        });

        if (result?.succeeded === true) {
          message.success('API Token User was removed.');
        }
        else {
          message.error(result?.errors.join('\n'));
        }
      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('API Token User could not be removed.');
      })
      .finally(() => {
        this.setState({ loading: false });
        this.goToAPITokenUsers();
      });
  }

  private userRolesSaved = () => {
    this.toggleIsEditing(APITokenUserDetailsPage.API_TOKEN_USER_ROLES, false);
  }

  private goToAPITokenUsers = () => {
    HistoryUtil.push(Routes.generate(Routes.API_TOKEN_USERS));
  }

  private changeTab = (activeTab: string) => {
    this.setState({ activeTab: activeTab });
    HistoryUtil.replace(Routes.generate('', {}, { activeTab: activeTab }), { activeTab: activeTab });
  }

  render() {
    const title = this.state.loading ? '...' : this.state.apiTokenUserId == Guid.Empty() ? 'New User' : this.state.apiTokenUser.apiName ?? '...' as any;
    const subtitle = null;

    return (
      <Content >
        <HeaderPortal
          title={title}
          subTitle={subtitle}
          onBack={this.goToAPITokenUsers}
          breadcrumbs={Breadcrumbs.apiTokenUserDetails(title, this.state.apiTokenUserId)}
          extra={this.renderMoreActions()}
          footer={
            <Tabs activeKey={this.state.activeTab} key='content' onChange={this.changeTab}>
              <Tabs.TabPane key='details' tab='Details' />
              <Tabs.TabPane key='roles' tab='Roles' />
            </Tabs>
          }>
        </HeaderPortal>

        <PageStayPrompt when={this.isEditingCheck} />

        {this.renderDetailsCard()}
        {this.renderRolesCard()}
      </Content >
    );
  }

  renderDetailsCard() {
    if (this.state.activeTab === 'details') {
      if (this.state.editings) {
        const editButton = this.state.apiTokenUserId == Guid.Empty() ?
          null :
          <AuthorizedContent validFeatureFlags={[FeatureFlag.MANAGE_API_TOKEN_USER]}>
            <EditCancelButton onClick={() => this.toggleIsEditing(APITokenUserDetailsPage.API_TOKEN_USER_DETAILS)} isEditing={this.state.editings.get(APITokenUserDetailsPage.API_TOKEN_USER_DETAILS)} />
          </AuthorizedContent>;

        return (
          <Card title="Details" extra={editButton}>
            <APITokenUserDetailsForm
              ref={this._apiTokenUserDetailsRef}
              apiTokenUserId={this.state.apiTokenUserId}
              isEditing={this.state.editings.get(APITokenUserDetailsPage.API_TOKEN_USER_DETAILS)}
              onSave={this.userDetailsSaved} />
          </Card>
        );
      }
    }
  }

  renderRolesCard() {
    if (this.state.activeTab === 'roles') {
      if (this.state.apiTokenUserId != Guid.Empty()) {
        if (this.state.editings) {
          const editButton = this.state.apiTokenUserId == Guid.Empty() ?
            null :
            <AuthorizedContent validFeatureFlags={[FeatureFlag.MANAGE_API_TOKEN_USER]}>
              <EditCancelButton onClick={() => this.toggleIsEditing(APITokenUserDetailsPage.API_TOKEN_USER_ROLES)} isEditing={this.state.editings.get(APITokenUserDetailsPage.API_TOKEN_USER_ROLES)} />
            </AuthorizedContent>

          return (
            <Card title="Roles" extra={editButton}>
              <UserRolesForm
                ref={this._userRolesRef}
                userId={this.state.apiTokenUserId}
                isEditing={this.state.editings.get(APITokenUserDetailsPage.API_TOKEN_USER_ROLES)}
                onSave={this.userRolesSaved} />
            </Card >
          );
        }
      }
    }
  }

  renderMoreActions() {
    const menu = (
      <Menu>
        < Menu.Item title='Delete API Token User' key={'delete_' + this.state.apiTokenUserId} onClick={() => this.deleteConfirmation(this.state.apiTokenUserId)}  >
          Delete API Token User
        </Menu.Item>
      </Menu>
    );

    return (
      <AuthorizedContent validFeatureFlags={[FeatureFlag.MANAGE_API_TOKEN_USER]}>
        <Space direction="horizontal">
          <Dropdown overlay={menu}>
            <Button size='large' type='link' >
              <MoreOutlined />
            </Button>
          </Dropdown>
        </Space>
      </AuthorizedContent>
    );
  }
}

function mapStateToProps(state: StateStoreModel) {
  return {
    institution: state.Institution.Selected,
    currentUser: state.UserSession.Value
  };
}

export default withRouter(connect(mapStateToProps)(APITokenUserDetailsPage));
